import { DateTime } from 'luxon';
import type { FulfilmentDay, ShippingOptionContentVariantKeys, ShippingOptionContentVariants } from './types';

export function getContent<TContent extends ShippingOptionContentVariants>({
  content,
  variants,
  descriptionArgs,
}: {
  content: ShippingOptionContentVariants;
  variants: ShippingOptionContentVariantKeys;
  descriptionArgs: Parameters<TContent['descriptions']['A']>[0];
}) {
  return {
    icon: content.icons[variants.icon],
    title: content.titles[variants.title],
    description: content.descriptions[variants.description](descriptionArgs),
  };
}

// The day, relative to the customer's local date, that a same day option is fulfilled on. The backend decides it's today
// or tomorrow, so any other date (null) means the customer's clock or timezone doesn't line up with the pharmacy's
export const getFulfilmentDay = (expectedFulfilmentDate: string, localDate: string): FulfilmentDay | null => {
  if (expectedFulfilmentDate === localDate) {
    return 'today';
  }

  return expectedFulfilmentDate === DateTime.fromISO(localDate).plus({ days: 1 }).toISODate() ? 'tomorrow' : null;
};

// Will only include minutes if the minutes are not :00. eg if 5:00am would have been returned it will instead return 5am
export const get12HourFormat = (time: string) => {
  const dateTime = DateTime.fromISO(time);

  const hasMinutes = dateTime.minute !== 0;

  return `${dateTime.toFormat('h')}${hasMinutes ? `:${dateTime.toFormat('mm')}` : ''}${dateTime.toFormat('a').toLowerCase()}`;
};

// NOTE: Update to use localWeekday if we ever support more than Sat/Sun based weekend locales
export const WEEKEND_START = {
  weekdayLong: 'Friday',
  hour: 17, // 5pm as 24 hour time
} as const;

// Returns true if current time is before the weekend (between Monday at 12:00am and WEEKEND_START)
export const isBeforeWeekend = () => {
  const now = DateTime.now();

  if (now.isWeekend) {
    return false;
  }

  if (now.weekdayLong === WEEKEND_START.weekdayLong && now.hour >= 17) {
    return false;
  }

  return true;
};
